// File: src/pages/Chat.jsx

import React, { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import "../styles/Chat.css";

export default function Chat() {
  const { user, token } = useContext(AuthContext);
  const { id: recipientId } = useParams();

  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  // ✅ Fetch messages and mark them as read
  useEffect(() => {
    const fetchMessagesAndMarkRead = async () => {
      try {
        // 1. Fetch messages
        const res = await fetch(`https://globisun-app.onrender.com/api/chats/${recipientId}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data?.msg || "Error fetching messages");
        setMessages(data.messages || []);

        // 2. Mark them as read
        await fetch(`https://globisun-app.onrender.com/api/chats/${recipientId}/mark-as-read`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
      } catch (err) {
        console.error(err);
        setError("Failed to load messages. " + err.message);
      } finally {
        setLoading(false);
      }
    };

    if (recipientId && token) {
      fetchMessagesAndMarkRead();
    } else {
      setError("Invalid chat or user session.");
      setLoading(false);
    }
  }, [recipientId, token]);

  // ✅ Send message
  const sendMessage = async () => {
    if (!text.trim()) return;

    try {
      const res = await fetch(`https://globisun-app.onrender.com/api/chats/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          receiverId: recipientId,
          message: text,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.msg || "Error sending message");

      setMessages((prev) => [...prev, data.message]);
      setText("");
    } catch (err) {
      console.error(err);
      setError("Message not sent. " + err.message);
    }
  };

  return (
    <div className="chat-page">
      <h2>💬 Chat</h2>

      {loading ? (
        <p>Loading chat...</p>
      ) : error ? (
        <div style={{ color: "red", marginBottom: "1rem" }}>
          ⚠️ {error}
        </div>
      ) : (
        <>
          <div className="chat-box">
            {messages.length === 0 ? (
              <p className="no-messages">No messages yet</p>
            ) : (
              messages.map((msg, i) => (
                <div
                  key={i}
                  className={`message ${
                    msg.senderId === user?._id ? "sent" : "received"
                  }`}
                >
                  <p>{msg.message}</p>
                  <span className="timestamp">
                    {new Date(msg.createdAt).toLocaleTimeString()}
                  </span>
                </div>
              ))
            )}
          </div>

          <div className="chat-input">
            <input
              type="text"
              placeholder="Type your message..."
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            />
            <button onClick={sendMessage}>Send</button>
          </div>
        </>
      )}
    </div>
  );
}
